import { ButtonDropdown, Link } from '@cloudscape-design/components';
import { findInNavGraph, findInNavGraphV2 } from 'src/utils/findInNavGraph';
import { ArtifactCard } from 'src/components/ArtifactsSection';
import { useRecentlyViewed } from 'src/components/providers/RecentlyViewedProvider';
import {
    USE_NAV_GRAPH_V2,
    useNavGraph,
} from 'src/components/providers/NavGraphProvider';
import { useFavorites } from 'src/components/providers/FavoritesProvider';
import { useTranslate } from 'src/i18n/useTranslate';
import './artifact-card-header.scss';
export type ArtifactListCardProps = {
    item: ArtifactCard;
    testId: string;
    openInSameTab?: boolean;
};
const ArtifactCardHeader = ({
    item,
    testId,
    openInSameTab = false,
}: ArtifactListCardProps) => {
    const t = useTranslate();
    const { addRecentlyViewed } = useRecentlyViewed();
    const { navGraph } = useNavGraph();
    const { addFavorite, deleteFavorite, isFavorite } = useFavorites();

    const getImageUrl = (id: number, path: string | undefined) => {
        if (USE_NAV_GRAPH_V2) {
            const document = findInNavGraphV2(id, navGraph);
            return document?.pictureUrl || '';
        }

        if (!path) return '';
        const document = findInNavGraph(id, path, navGraph, 'id');
        return document?.pictureUrl || '';
    };

    const handleTitleOnFollow = (item: ArtifactCard) => {
        if (item.externalUrl) {
            addRecentlyViewed(item.id, item.topicId, item.topicName);
        }
    };
    return (
        <div data-list-page-card data-testid={testId}>
            <div className="artifact-card-header-title-info">
                {getImageUrl(item.id, item.breadcrumbPath) && (
                    <img
                        alt={item.description}
                        src={getImageUrl(item.id, item.breadcrumbPath)}
                        style={{
                            width: '50px',
                            marginRight: '20px',
                        }}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                )}
                <Link
                    href={
                        item.externalUrl
                            ? item.externalUrl.replace('http://', 'https://')
                            : item.topicId
                            ? `/${item.id}#/${item.topicId}/${item.topicName}`
                            : `/${item.id}`
                    }
                    onFollow={(e) => {
                        if (!item.externalUrl) {
                            e.preventDefault();
                            const href = item.topicId
                                ? `/${item.id}#/${item.topicId}/${item.topicName}`
                                : `/${item.id}`;
                            window.location.href = href;
                        }
                        handleTitleOnFollow(item);
                    }}
                    target={
                        item.externalUrl && !openInSameTab ? '_blank' : '_self'
                    }
                    fontSize="heading-m"
                >
                    {item.title}
                </Link>
            </div>
            <ButtonDropdown
                onItemClick={({ detail }) => {
                    const id = detail.id;

                    switch (id) {
                        case 'favorite':
                            deleteFavorite(item.id);
                            break;
                        case 'not-favorite':
                            addFavorite(item.id, item.topicId, item.topicName);
                            break;
                        case 'copy-link':
                            const copyUrl = item.topicId
                                ? `${window.location.origin}/${item.id}#/${item.topicId}/${item.topicName}`
                                : `${window.location.origin}/${item.id}`;
                            navigator.clipboard.writeText(copyUrl);
                            break;

                        default:
                            break;
                    }
                }}
                data-list-page-card-dropdown-container
                data-testid={'article-button-dropdown'}
                items={[
                    {
                        id: 'copy-link',
                        text: t('landing_page_card_options_copy', 'Copy link'),
                        iconName: 'copy',
                    },
                    {
                        id: isFavorite(item.id) ? 'favorite' : 'not-favorite',
                        text: isFavorite(item.id)
                            ? t(
                                  'remove-from-favorites',
                                  'Remove from Favorites'
                              )
                            : t('add-to-favorites', 'Add to Favorites'),
                        iconName: 'heart',
                    },
                ]}
                ariaLabel={t('control_instance', 'Control instance')}
                variant="inline-icon"
            />
        </div>
    );
};

export default ArtifactCardHeader;
